import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowLeft, Radio, Send, Loader2, AlertTriangle, CheckCircle2,
  Clock, MapPin, Users, Handshake, Wallet as WalletIcon, Heart,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import MyEventSidebar from "./MyEventSidebar";

const POLL_ACTIVE_MS = 10_000;
const POLL_IDLE_MS = 30_000;
const IDLE_THRESHOLD_MS = 5 * 60_000;

const QUICK_SUGGESTIONS = [
  "Tout va bien ✓",
  "Prêt à commencer ✓",
  "Retard de 15 min",
];

const TECHNICAL_RE = /^(test[_\s-]|iter\d+|fixture|demo[_\s-]|debug|__)/i;
const cleanName = (s) => {
  if (!s) return "Votre mariage";
  const c = String(s).replace(/^\s*(test[_\s-]+|demo[_\s-]+)/i, "").replace(/\s+[a-f0-9]{6,}$/i, "").trim();
  return TECHNICAL_RE.test(c) ? "Votre mariage" : c;
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso || ""; } };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function MyEventLive() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [snap, setSnap] = useState(null);
  const [messages, setMessages] = useState([]);
  const [err, setErr] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [alertSending, setAlertSending] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const scrollRef = useRef(null);
  const lastTsRef = useRef(null);

  const nextItem = useMemo(() => {
    const items = (snap && snap.planning_items) || [];
    if (!items.length) return null;
    try {
      const now = new Date();
      const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      return items.find((i) => (i.heure || "") >= hhmm) || items[0];
    } catch { return items[0]; }
  }, [snap]);

  // ---- Initial snapshot ----
  useEffect(() => {
    if (!eventId) return;
    api.get(`/next/myevent/${eventId}/live`)
      .then((r) => setSnap(r.data))
      .catch((e) => setErr(e?.response?.status || 500));
    api.get(`/next/myevent/${eventId}/live/messages`, { params: { limit: 50 } })
      .then((r) => {
        const msgs = r.data?.messages || [];
        setMessages(msgs);
        if (msgs.length) lastTsRef.current = msgs[msgs.length - 1].created_at;
      })
      .catch(() => {});
  }, [eventId]);

  // ---- Polling with hidden-tab pause + idle backoff ----
  useEffect(() => {
    if (!eventId || err) return;
    let timer;
    const tick = async () => {
      if (document.hidden) return;
      try {
        const params = { limit: 50 };
        if (lastTsRef.current) params.since = lastTsRef.current;
        const r = await api.get(`/next/myevent/${eventId}/live/messages`, { params });
        const incoming = r.data?.messages || [];
        if (incoming.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const merged = [...prev];
            for (const m of incoming) if (!seen.has(m.id)) merged.push(m);
            return merged.slice(-200);
          });
          lastTsRef.current = incoming[incoming.length - 1].created_at;
          lastActivityRef.current = Date.now();
        }
      } catch {}
    };
    const schedule = () => {
      const idle = Date.now() - lastActivityRef.current > IDLE_THRESHOLD_MS;
      const delay = document.hidden ? POLL_IDLE_MS : (idle ? POLL_IDLE_MS : POLL_ACTIVE_MS);
      timer = setTimeout(async () => { await tick(); schedule(); }, delay);
    };
    schedule();
    const onVis = () => { if (!document.hidden) { lastActivityRef.current = Date.now(); tick(); } };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearTimeout(timer); document.removeEventListener("visibilitychange", onVis); };
  }, [eventId, err]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  if (!user) return <Navigate to="/connexion" replace />;
  if (err === 403) return <Err title="Accès refusé" text="Vous n'êtes pas membre de cet événement." testid="live-forbidden" />;
  if (err === 404) return <Err title="Événement introuvable" text="Cet événement n'existe pas." testid="live-notfound" />;
  if (!snap) return <div className="min-h-screen bg-black text-white p-8"><p className="text-white/50 italic">Chargement…</p></div>;

  const ev = snap.event || {};
  const couple = cleanName(ev.name);
  const isProvider = !!snap.is_provider;
  const canAlert = snap.role === "ORGANIZER" || snap.role === "CO_ORGANIZER" ||
                    snap.role === "ADMIN" || snap.role === "SUPER_ADMIN";
  const jMinus = ev.j_minus;
  const jLabel = jMinus == null ? "" : jMinus === 0 ? "Aujourd'hui" : jMinus > 0 ? `J − ${jMinus}` : `J + ${Math.abs(jMinus)}`;

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setSending(true);
    lastActivityRef.current = Date.now();
    try {
      const r = await api.post(`/next/myevent/${eventId}/live/messages`, { content: q });
      const msg = r.data?.message;
      if (msg) {
        setMessages((prev) => [...prev, msg]);
        lastTsRef.current = msg.created_at;
      }
      setInput("");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Envoi impossible.";
      toast.error(detail);
    } finally { setSending(false); }
  };

  const sendAlert = async () => {
    const q = alertText.trim();
    if (!q || alertSending) return;
    setAlertSending(true);
    try {
      const r = await api.post(`/next/myevent/${eventId}/live/alert`, { content: q });
      const msg = r.data?.alert;
      if (msg) { setMessages((prev) => [...prev, msg]); lastTsRef.current = msg.created_at; }
      toast.success(`Alerte envoyée · ${r.data?.fanout_count || 0} destinataire(s).`);
      setAlertText("");
      setShowAlert(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Alerte impossible.");
    } finally { setAlertSending(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="myevent-live">
      <header className="border-b border-[#DCB457]/15 bg-black/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to={`/myevent/${eventId}`} data-testid="live-back" className="text-xs uppercase tracking-widest text-white/60 hover:text-[#DCB457] inline-flex items-center gap-2">
            <ArrowLeft size={13} /> MyEvent
          </Link>
          <span className="text-[10px] uppercase tracking-[0.24em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1 inline-flex items-center gap-1.5">
            <Radio size={11} /> MyLive
          </span>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-8 lg:pt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <MyEventSidebar />
        <main className="lg:col-span-9 min-w-0 space-y-6">
          {/* Hero */}
          <section>
            <p className="eyebrow">MyEvent · Module</p>
            <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">
              Le <span className="italic gold-text">Jour J.</span>
            </h1>
            <p className="mt-3 text-white/60 text-sm md:text-base">
              Coordination en direct pour ce mariage — planning, prestataires, messages.
            </p>
            <p className="mt-1 text-[#DCB457] text-sm" data-testid="live-event-summary">
              {couple}{ev.date && ` — ${fmtDate(ev.date)}`}{ev.lieu && ` · ${ev.lieu}`}
            </p>
          </section>

          {/* KPIs */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="live-status">
            <Kpi label="Compte à rebours" value={jLabel || "—"} accent={jMinus === 0} />
            <Kpi label="Étapes prévues" value={(snap.planning_items || []).length} />
            <Kpi label="Prestataires" value={isProvider ? "—" : (snap.providers || []).length} />
            <Kpi
              label={isProvider ? "Rôle" : "Budget restant"}
              value={isProvider ? snap.role : (snap.wallet ? fmtEur(snap.wallet.remaining) : "—")}
            />
          </section>

          {/* Next milestone */}
          {nextItem && (
            <section className="card-luxe p-5 border-[#DCB457]/40" data-testid="live-next-milestone">
              <p className="eyebrow flex items-center gap-2"><Clock size={11} /> Prochaine étape</p>
              <p className="font-serif-luxe text-2xl mt-2">
                {nextItem.heure && <span className="gold-text mr-3">{nextItem.heure}</span>}
                {nextItem.label || "—"}
              </p>
              {nextItem.notes && <p className="text-sm text-white/60 mt-1">{nextItem.notes}</p>}
            </section>
          )}

          {/* Timeline */}
          <section data-testid="live-timeline">
            <p className="eyebrow flex items-center gap-2"><Heart size={11} /> Timeline du jour</p>
            <div className="card-luxe mt-3">
              {(snap.planning_items || []).length === 0 ? (
                <p className="p-6 text-white/45 italic text-sm">Aucune étape planifiée pour l'instant.</p>
              ) : (
                <div className="divide-y divide-[#DCB457]/10">
                  {(snap.planning_items || []).map((it, idx) => (
                    <div key={idx} data-testid={`live-timeline-item-${idx}`} className="flex gap-4 px-5 py-3">
                      <span className="text-[#DCB457] font-serif-luxe text-lg shrink-0 w-16">{it.heure || "—"}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-white/90 text-sm">{it.label || "—"}</p>
                        {it.notes && <p className="text-white/50 text-xs mt-0.5 truncate">{it.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Providers — masked for PROVIDER role */}
          {!isProvider && (
            <section data-testid="live-providers">
              <p className="eyebrow flex items-center gap-2"><Handshake size={11} /> Prestataires concernés</p>
              <div className="card-luxe mt-3">
                {(snap.providers || []).length === 0 ? (
                  <p className="p-6 text-white/45 italic text-sm">Aucun prestataire renseigné.</p>
                ) : (
                  <div className="divide-y divide-[#DCB457]/10">
                    {(snap.providers || []).map((p, i) => (
                      <div key={i} data-testid={`live-provider-${i}`} className="flex items-center justify-between px-5 py-3">
                        <div>
                          <p className="text-sm text-white/90">{p.name}</p>
                          {p.specialty && <p className="text-[11px] text-white/50">{p.specialty}</p>}
                        </div>
                        {p.status && (
                          <span className="text-[10px] uppercase tracking-widest text-[#DCB457] border border-[#DCB457]/40 px-2 py-0.5">
                            {p.status}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Alert bar (ORG/CO_ORG only) */}
          {canAlert && (
            <section className="card-luxe p-5 border-amber-400/40" data-testid="live-alert-bar">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="eyebrow flex items-center gap-2"><AlertTriangle size={11} className="text-amber-300" /> Alerte importante</p>
                  <p className="text-sm text-white/70 mt-1">Envoie une notification à tous les membres (cloche + email + push).</p>
                </div>
                <button
                  onClick={() => setShowAlert((v) => !v)}
                  data-testid="live-alert-toggle"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-amber-400/50 text-amber-300 hover:bg-amber-400/10 text-xs uppercase tracking-widest"
                >
                  <AlertTriangle size={13} /> {showAlert ? "Annuler" : "Alerte importante"}
                </button>
              </div>
              {showAlert && (
                <div className="mt-4 flex flex-col md:flex-row gap-3" data-testid="live-alert-panel">
                  <input
                    value={alertText}
                    onChange={(e) => setAlertText(e.target.value)}
                    maxLength={500}
                    placeholder="Message de l'alerte (max 500 caractères)…"
                    data-testid="live-alert-input"
                    className="flex-1 bg-black/50 border border-amber-400/30 focus:border-amber-400 outline-none px-3 py-2 text-sm text-white/90"
                  />
                  <button
                    onClick={sendAlert}
                    disabled={!alertText.trim() || alertSending}
                    data-testid="live-alert-send"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400 text-black hover:bg-amber-300 text-xs uppercase tracking-widest disabled:opacity-40"
                  >
                    {alertSending ? <Loader2 size={13} className="animate-spin" /> : <AlertTriangle size={13} />} Envoyer l'alerte
                  </button>
                </div>
              )}
            </section>
          )}

          {/* Coordination messages */}
          <section data-testid="live-messages-section">
            <p className="eyebrow flex items-center gap-2"><Users size={11} /> Coordination</p>
            <div className="card-luxe mt-3 flex flex-col" style={{ minHeight: "360px" }}>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-2" data-testid="live-messages" style={{ maxHeight: "50vh" }}>
                {messages.length === 0 ? (
                  <p className="text-white/45 italic text-sm text-center py-10" data-testid="live-messages-empty">
                    Aucun message pour le moment.
                  </p>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      data-testid={`live-msg-${m.id}`}
                      className={`px-4 py-2.5 ${m.kind === "alert"
                        ? "border-l-2 border-amber-400 bg-amber-400/10"
                        : "border-l-2 border-[#DCB457]/30 bg-black/40"}`}
                    >
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[#DCB457] font-serif-luxe text-sm">{m.author_name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-white/40">{m.author_role}</span>
                        {m.kind === "alert" && (
                          <span className="text-[9px] uppercase tracking-widest text-amber-300 border border-amber-400/40 px-1.5 py-0.5">
                            ALERTE
                          </span>
                        )}
                        <span className="text-[10px] text-white/40 ml-auto">{fmtTime(m.created_at)}</span>
                      </div>
                      <p className="text-sm text-white/85 mt-1 whitespace-pre-wrap">{m.content}</p>
                    </div>
                  ))
                )}
              </div>

              {/* Quick suggestions + input */}
              {messages.length === 0 && (
                <div className="px-4 md:px-6 py-3 border-t border-[#DCB457]/10 flex flex-wrap gap-2" data-testid="live-suggestions">
                  {QUICK_SUGGESTIONS.map((s, idx) => (
                    <button
                      key={idx}
                      onClick={() => send(s)}
                      data-testid={`live-suggestion-${idx}`}
                      className="text-[11px] px-3 py-1.5 border border-[#DCB457]/30 text-[#DCB457]/90 hover:bg-[#DCB457]/10"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="border-t border-[#DCB457]/15 p-3 md:p-4 flex items-end gap-2"
              >
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                  placeholder="Message court…"
                  maxLength={500}
                  data-testid="live-input"
                  className="flex-1 bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm text-white focus:border-[#DCB457] focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  data-testid="live-send"
                  className="shrink-0 w-11 h-11 bg-[#DCB457] text-black hover:bg-[#e6c454] transition disabled:opacity-40 flex items-center justify-center"
                  aria-label="Envoyer"
                >
                  <Send size={16} strokeWidth={1.5} />
                </button>
              </form>
            </div>
          </section>

          <p className="text-[10px] uppercase tracking-widest text-white/30 italic" data-testid="live-disclaimer">
            MyLive rafraîchit les messages toutes les 10 secondes. Pas d'appel LLM, aucune donnée d'un autre mariage.
          </p>
        </main>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="card-luxe p-4">
      <p className="eyebrow">{label}</p>
      <p className={`font-serif-luxe text-2xl mt-2 ${accent ? "gold-text" : ""}`}>{value}</p>
    </div>
  );
}

function Err({ title, text, testid }) {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
      <div className="max-w-md text-center" data-testid={testid}>
        <p className="eyebrow flex items-center justify-center gap-2"><Radio size={12} /> MyLive</p>
        <h1 className="font-serif-luxe text-3xl mt-3 mb-3">{title}</h1>
        <p className="text-white/60 text-sm">{text}</p>
      </div>
    </div>
  );
}
